"use client"
import { useDrag, useDrop } from "react-dnd"
import { FiFile, FiTrash2 } from "react-icons/fi"
import { MdDragIndicator } from "react-icons/md"
import PDFMerger from "./PDFMerger"

const PDFItem = ({ file, index, onRemoveFile, onMoveFile }) => {
  const [{ isDragging }, drag, dragPreview] = useDrag({
    type: "PDF_ITEM",
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const [, drop] = useDrop({
    accept: "PDF_ITEM",
    hover: (item, monitor) => {
      if (item.index !== index) {
        onMoveFile(item.index, index)
        item.index = index
      }
    },
  })

  return (
    <div
      ref={(node) => drop(dragPreview(node))}
      className={`flex items-center p-3 mb-2 bg-white border rounded-lg ${isDragging ? "opacity-50" : "opacity-100"}`}
    >
      <div ref={drag} className="cursor-move mr-2">
        <MdDragIndicator className="text-gray-400 h-5 w-5" />
      </div>
      <div className="flex items-center flex-1">
        <FiFile className="text-blue-500 h-5 w-5 mr-2" />
        <span className="text-sm text-gray-700 truncate flex-1">
          {index + 1}. {file.name}
        </span>
      </div>
      <button
        onClick={() => onRemoveFile(file.id)}
        className="text-red-500 hover:text-red-700 p-1"
        aria-label="Remove file"
      >
        <FiTrash2 className="h-5 w-5" />
      </button>
    </div>
  )
}

const PDFList = ({ files, onRemoveFile, onMoveFile, onClearAll, isLoading, setIsLoading, error, setError }) => {
  if (files.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No PDF files uploaded yet.</p>
        <p className="text-sm mt-2">Upload PDF files to merge them.</p>
      </div>
    )
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-700">PDF Files ({files.length})</h2>
        <div className="flex space-x-2">
          <button
            onClick={onClearAll}
            className="px-3 py-1 text-sm text-red-600 hover:text-red-800 border border-red-600 hover:border-red-800 rounded"
          >
            Clear All
          </button>
          <PDFMerger
            files={files}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            error={error}
            setError={setError}
          />
        </div>
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
        {files.map((file, index) => (
          <PDFItem key={file.id} file={file} index={index} onRemoveFile={onRemoveFile} onMoveFile={onMoveFile} />
        ))}
      </div>

      {error && <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-lg">{error}</div>}
    </div>
  )
}

export default PDFList
